/**
 * The Screen Studio's typed journey model: the shape ScreenConfigValidator compiles. The closed lists
 * (kinds, evidence kinds, field types and so on) come from GET /v1/screens/vocabulary and legality comes
 * back as server diagnostics, so nothing here decides what is allowed. This file holds only the shapes,
 * the copy, and the defaults a new screen starts from.
 */

/** `{en, hi, ar}` with en required, or `{key}` on the hub's content ladder. */
export interface LocalizedText {
  en?: string;
  hi?: string;
  ar?: string;
  key?: string;
}

export interface ScreenUi {
  text?: Record<string, LocalizedText>;
  prompts?: LocalizedText[];
  accept?: string[];
}

export interface JourneyBlock {
  sequential?: boolean;
  header?: {
    heading?: LocalizedText;
    subheading?: LocalizedText;
    showCaseRef?: boolean;
    showApplicantName?: boolean;
  };
  progress?: { style?: string };
  pages?: JourneyPages;
}

/** One of the studio's pages (OL-23). The variants are the server's; plain text only. */
export interface PageDef {
  variant: string;
  heading?: LocalizedText;
  body?: LocalizedText;
  startLabel?: LocalizedText;
  continueLabel?: LocalizedText;
  nextSteps?: LocalizedText[];
}

export type PageName = 'welcome' | 'terms' | 'final';
export type JourneyPages = Partial<Record<PageName, PageDef>>;

export const PAGE_COPY: Record<PageName, { label: string; hint: string }> = {
  welcome: { label: 'Welcome page', hint: 'Shown first, until the applicant has started.' },
  terms: { label: 'Terms page', hint: 'Shown before the steps.' },
  final: { label: 'Final page', hint: 'Shown when every required step is done. It says nothing about the outcome.' },
};

export const VARIANT_COPY: Record<string, string> = {
  SIMPLE: 'Simple: heading, words, button',
  CHECKLIST: 'Checklist: also lists the steps',
  HERO: 'Hero: centred, with the large mark',
  NOTICE: 'Notice: read and continue, records nothing',
  CONSENT: 'Consent: accept the first declaration',
  THANK_YOU: 'Thank you',
  NEXT_STEPS: 'Next steps: a short list',
  STATUS: 'Status: shows the progress view',
};

export interface PromptField {
  name: string;
  label?: string;
  /** Room-rendered input type (server-gated FIELD_TYPES; absent = text). */
  type?: string;
  /** For type 'select' — each string is both value and label (PS-5 PromptField). */
  options?: string[];
  required?: boolean;
  /** Named section this field renders under; consecutive fields sharing one form a group. */
  section?: string;
  placeholder?: string;
  helpText?: string;
  /** Per-locale strings: `i18n.label.hi`. The plain property is the English. */
  i18n?: Record<string, Record<string, string>>;
  [k: string]: unknown; // forward-compatible: unknown props survive the round-trip untouched
}

export interface ScreenDef {
  id?: string;
  kind?: string;
  title?: string;
  titleKey?: string;
  whyKey?: string;
  helpKey?: string;
  evidenceKind?: string;
  layoutSlot?: string;
  prompt?: PromptField[];
  spec?: Record<string, unknown>;
  optional?: boolean;
  ui?: ScreenUi;
  [k: string]: unknown; // forward-compatible: unknown fields survive the round-trip untouched
}

export interface JourneyDef {
  locales?: string[];
  journey?: JourneyBlock;
  screens?: ScreenDef[];
  [k: string]: unknown;
}

/** What each kind is called and what it does. Which kinds exist is the server's answer. */
export const KIND_COPY: Record<string, { label: string; description: string }> = {
  UPLOAD: {
    label: 'Document upload',
    description: 'The applicant provides a file. Pin the evidence kind so the case file stays typed.',
  },
  FORM: {
    label: 'Form / input screen',
    description: 'Structured questions: you define the fields, the applicant types the answers.',
  },
  ACKNOWLEDGE: {
    label: 'Confirmation',
    description: 'The applicant confirms a statement. Writes an acknowledgement, not a consent record.',
  },
  DECLARATION: {
    label: 'Declaration (consent)',
    description: 'The applicant reads a versioned text and accepts it. Writes a consent record with the text hash.',
  },
  PARTIES: {
    label: 'Related parties',
    description: 'The applicant lists directors, owners, officers or contacts, with ownership where you ask for it.',
  },
  CAPTURE: {
    label: 'Camera capture',
    description: 'A selfie or a short face video. Liveness scoring runs in the flow, never in the room.',
  },
  OTP: {
    label: 'One-time code',
    description: 'Verifies a mobile number or email with a code. Needs a communication provider on this hub.',
  },
  IDENTIFIER: {
    label: 'Identification number',
    description: 'PAN, GSTIN, CIN and others, format-checked as typed. The registry lookup runs in the flow.',
  },
  BANK_DETAILS: {
    label: 'Bank details',
    description: 'Account and IFSC, or a VPA. Penny drop and name match run in the flow.',
  },
  AADHAAR_OTP: {
    label: 'Aadhaar e-KYC',
    description: 'Consent, the number (masked, never stored) and the code. Needs a live e-KYC entitlement on this hub.',
  },
};

/** The label and hint for a text slot. A slot with no entry here still works, under its own name. */
export const SLOT_COPY: Record<string, { label: string; hint: string }> = {
  why: { label: 'Why we ask', hint: 'Shown under the title on every step. Empty uses the room\'s wording for this kind.' },
  help: { label: 'Help text', hint: 'A line of guidance under the reason. Empty shows nothing.' },
  submit: { label: 'Main button', hint: 'The button that completes the step.' },
  send: { label: 'Send button', hint: 'The button that asks for the code.' },
  dropTitle: { label: 'Upload prompt', hint: 'The large line in the drop area.' },
  dropHint: { label: 'Upload hint', hint: 'The small line under it.' },
  statement: { label: 'Statement', hint: 'What the applicant is confirming.' },
  readPrompt: { label: 'Reading prompt', hint: 'The line above the declaration text.' },
  intro: { label: 'Introduction', hint: 'The line at the top of the step.' },
  add: { label: 'Add button', hint: 'The button that adds another person.' },
  contactLabel: { label: 'Contact label', hint: 'The label on the phone or email field.' },
  contactPlaceholder: { label: 'Contact placeholder', hint: 'The example inside the field.' },
  codeLabel: { label: 'Code label', hint: 'The label on the code field.' },
  label: { label: 'Field label', hint: 'The label on the number field.' },
  placeholder: { label: 'Placeholder', hint: 'The example inside the field.' },
  holderLabel: { label: 'Account holder label', hint: '' },
  accountLabel: { label: 'Account number label', hint: '' },
  confirmLabel: { label: 'Confirm account label', hint: '' },
  ifscLabel: { label: 'IFSC label', hint: '' },
  vpaLabel: { label: 'VPA label', hint: '' },
  numberLabel: { label: 'Aadhaar number label', hint: '' },
};

export const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  textarea: 'Multi-line text',
  number: 'Number',
  date: 'Date',
  select: 'Dropdown (select)',
  tel: 'Phone',
  email: 'Email',
};

export const PROGRESS_LABELS: Record<string, string> = {
  COUNT: 'A line: "2 of 5 done"',
  BAR: 'A progress bar',
  STEPS: 'Numbered steps',
  NONE: 'Nothing',
};

/** A CRA check family as a kind plus config. The server knows only kinds (design doc, section 3). */
export interface CheckPreset {
  family: string;
  label: string;
  kind: string;
  id: string;
  title: string;
  patch: Partial<ScreenDef>;
}

const identifier = (family: string, label: string, scheme: string): CheckPreset => ({
  family,
  label,
  kind: 'IDENTIFIER',
  id: scheme.toLowerCase(),
  title: `Your ${label}`,
  patch: { spec: { scheme } },
});
const CONSENT_TEXT = { version: '1.0', textKey: 'aadhaar.consent.v1' };

export const CHECK_PRESETS: CheckPreset[] = [
  identifier('pan-check', 'PAN', 'PAN'),
  identifier('pan-aadhaar-link-check', 'PAN (Aadhaar link check)', 'PAN'),
  identifier('gst-check', 'GSTIN', 'GSTIN'),
  identifier('cin-check', 'CIN', 'CIN'),
  identifier('uam-check', 'Udyam number', 'UDYAM'),
  identifier('voter-id-check', 'Voter ID', 'VOTER'),
  identifier('passport-validation', 'Passport number', 'PASSPORT'),
  { family: 'bank-check', label: 'Bank account', kind: 'BANK_DETAILS', id: 'bank', title: 'Your bank account', patch: { spec: { mode: 'ACCOUNT_IFSC' } } },
  { family: 'upi-validation', label: 'UPI ID', kind: 'BANK_DETAILS', id: 'upi', title: 'Your UPI ID', patch: { spec: { mode: 'VPA' } } },
  { family: 'sms-otp-verification', label: 'SMS code', kind: 'OTP', id: 'sms-otp', title: 'Verify your mobile number', patch: { spec: { channel: 'SMS' } } },
  { family: 'email-otp-check', label: 'Email code', kind: 'OTP', id: 'email-otp', title: 'Verify your email', patch: { spec: { channel: 'EMAIL' } } },
  { family: 'otp-aadhar-check', label: 'Aadhaar OTP', kind: 'AADHAAR_OTP', id: 'aadhaar', title: 'Verify your Aadhaar', patch: { spec: CONSENT_TEXT } },
  { family: 'aadhaar-aggregate', label: 'Aadhaar aggregate', kind: 'AADHAAR_OTP', id: 'aadhaar', title: 'Verify your Aadhaar', patch: { spec: CONSENT_TEXT } },
  { family: 'aadhaar-doc-otp', label: 'Aadhaar document OTP', kind: 'AADHAAR_OTP', id: 'aadhaar', title: 'Verify your Aadhaar', patch: { spec: CONSENT_TEXT } },
  { family: 'selfi-check', label: 'Selfie', kind: 'CAPTURE', id: 'selfie', title: 'Take a selfie', patch: { spec: { mode: 'SELFIE' } } },
  { family: 'liveliness', label: 'Liveness video', kind: 'CAPTURE', id: 'liveness', title: 'Record a short video', patch: { spec: { mode: 'VIDEO', maxSeconds: 10 }, ui: { prompts: [{ en: 'Look at the camera' }, { en: 'Turn your head slowly' }] } } },
  { family: 'video-verification-check', label: 'Video capture', kind: 'CAPTURE', id: 'video', title: 'Record a short video', patch: { spec: { mode: 'VIDEO', maxSeconds: 15 } } },
  { family: 'document-check', label: 'Document', kind: 'UPLOAD', id: 'document', title: 'Upload a document', patch: { evidenceKind: 'DATAROOM_DOCUMENT' } },
  { family: 'id-doc-extraction', label: 'ID document', kind: 'UPLOAD', id: 'id-document', title: 'Upload your ID document', patch: { evidenceKind: 'IDENTITY_DOCUMENT' } },
  {
    family: 'address-check',
    label: 'Address',
    kind: 'FORM',
    id: 'address',
    title: 'Your address',
    patch: {
      prompt: [
        { name: 'addressLine', label: 'Address', type: 'textarea', required: true },
        { name: 'city', label: 'City', required: true },
        { name: 'postalCode', label: 'Postal code', required: true },
      ],
    },
  },
];

/** Slug a human title into a legal screen id / field name seed. */
export function slugify(text: string, maxLen = 40): string {
  const slug = text
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, maxLen)
    .replace(/-+$/g, '');
  return slug || 'screen';
}

/** camelCase a label into a field-name seed (annual income → annualIncome). */
export function fieldNameFrom(label: string): string {
  const words = label.trim().split(/[^a-zA-Z0-9]+/).filter(Boolean);
  if (words.length === 0) return 'field';
  const name = words
    .map((w, i) => (i === 0 ? w.toLowerCase() : w[0].toUpperCase() + w.slice(1).toLowerCase()))
    .join('')
    .replace(/^[0-9]+/, '');
  return name || 'field';
}

/** A unique id among siblings: base, base-2, base-3… */
export function uniqueId(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * A deep copy of an existing screen as a NEW journey entry: identical configuration, fresh
 * unique id (the gate refuses duplicate ids). The title is kept verbatim — the copy sits right
 * next to its original, so a suffix would only be noise the author deletes.
 */
export function duplicateScreen(screen: ScreenDef, existing: ScreenDef[]): ScreenDef {
  const copy = JSON.parse(JSON.stringify(screen)) as ScreenDef;
  const taken = new Set(existing.map((s) => s.id ?? ''));
  copy.id = uniqueId(copy.id || slugify(copy.title ?? ''), taken);
  return copy;
}

/** A fresh screen of the given kind with sensible, immediately-editable defaults. */
export function newScreen(kind: string, existing: ScreenDef[]): ScreenDef {
  const taken = new Set(existing.map((s) => s.id ?? ''));
  const base = (id: string, title: string, rest: Partial<ScreenDef> = {}): ScreenDef => ({
    id: uniqueId(id, taken),
    kind,
    title,
    ...rest,
  });
  switch (kind) {
    case 'UPLOAD':
      return base('upload-document', 'Upload a document', {
        evidenceKind: 'DATAROOM_DOCUMENT',
        layoutSlot: 'PRIMARY',
      });
    case 'FORM':
      return base('details', 'Your details', {
        prompt: [{ name: 'fullName', label: 'Full legal name' }],
      });
    case 'DECLARATION':
      return base('declaration', 'Your declaration', {
        spec: { version: '1.0', text: 'I confirm that the information I provide is true and complete.' },
      });
    case 'PARTIES':
      return base('parties', 'Directors and owners', {
        spec: { roles: ['DIRECTOR', 'UBO'], minParties: 1, maxParties: 20, requireOwnershipFor: ['UBO'] },
      });
    case 'CAPTURE':
      return base('selfie', 'Take a selfie', { spec: { mode: 'SELFIE' } });
    case 'OTP':
      return base('verify-mobile', 'Verify your mobile number', { spec: { channel: 'SMS' } });
    case 'IDENTIFIER':
      return base('pan', 'Your PAN', { spec: { scheme: 'PAN' } });
    case 'BANK_DETAILS':
      return base('bank', 'Your bank account', { spec: { mode: 'ACCOUNT_IFSC' } });
    case 'AADHAAR_OTP':
      return base('aadhaar', 'Verify your Aadhaar', {
        spec: { version: '1.0', textKey: 'aadhaar.consent.v1' },
      });
    case 'ACKNOWLEDGE':
      return base('confirm', 'Confirm and submit');
    default:
      // A kind the server has and this console has no defaults for: the gate says what it needs.
      return base(slugify(kind.toLowerCase()), kind);
  }
}

/** A new screen from a check preset. */
export function screenFromPreset(preset: CheckPreset, existing: ScreenDef[]): ScreenDef {
  const taken = new Set(existing.map((s) => s.id ?? ''));
  return {
    id: uniqueId(preset.id, taken),
    kind: preset.kind,
    title: preset.title,
    ...(JSON.parse(JSON.stringify(preset.patch)) as Partial<ScreenDef>),
  };
}

/** Set one localized text in `ui.text`, dropping empty containers so the JSON stays minimal. */
export function withUiText(screen: ScreenDef, slot: string, value: LocalizedText | undefined): ScreenDef {
  const text = { ...(screen.ui?.text ?? {}) };
  if (value) text[slot] = value;
  else delete text[slot];
  const ui: ScreenUi = { ...(screen.ui ?? {}), text };
  if (Object.keys(text).length === 0) delete ui.text;
  const next: ScreenDef = { ...screen, ui };
  if (Object.keys(ui).length === 0) delete next.ui;
  return next;
}

/**
 * The title's translation for one locale. The plain `title` stays the English, and `ui.text.title`
 * carries it too once there is a translation, because a localized text must name its English.
 */
export function withTitleTranslation(screen: ScreenDef, locale: string, value: string): ScreenDef {
  const current = { ...(screen.ui?.text?.title ?? {}) } as LocalizedText;
  if (value) (current as Record<string, string>)[locale] = value;
  else delete (current as Record<string, string | undefined>)[locale];
  const translated = (['hi', 'ar'] as const).some((l) => current[l]);
  return withUiText(screen, 'title', translated ? { ...current, en: screen.title ?? '' } : undefined);
}

/** The English title, kept in step wherever it is held. */
export function withTitle(screen: ScreenDef, title: string): ScreenDef {
  const next: ScreenDef = { ...screen, title };
  const held = screen.ui?.text?.title;
  return held ? withUiText(next, 'title', { ...held, en: title }) : next;
}
